import React, { FC, useState } from 'react';
import useLocalStorage from 'utils/hooks/useLocalStorage';
import { PostPreviewVariant } from '@kontentino/kontentino-post-previews';
import Skeleton from 'components/posts/detail/overview/leftSection/preview/Skeleton';
import { PostDetail } from 'types/PostDetail';
import { queryKey } from 'constants/queryKey';
import PostApi from 'api/post';
import { ActivityIndicator, Alert } from '@kontentino/ui';
import PostPreviewWithVariants from 'app/components/PostPreviewWithVariants';
import { BrowserStorage } from 'utils/browserStorage';
import { useUser } from 'modules/user/userSelector';
import { useTranslation } from 'react-i18next';
import QuickPostEditDateTime from '../QuickPostEditDateTime';
import useQueryNoCache from 'utils/hooks/useQueryNoCache';
import { PageNumberType } from '@kontentino/kontentino-constants/Pages';
import { ApiClientError } from 'api/client';
import { coerceToPreviewAttachments } from 'utils/previewCoercion';

type Props = {
  id: number;
  loadingType?: 'skeleton' | 'indicator';
  style?: React.CSSProperties;
  onLoaded?: AnyFunction;
};

const Preview: FC<Props> = ({
  id,
  loadingType,
  style: inlineStyle,
  onLoaded,
}) => {
  const { data, isLoading, isError, error } = useQueryNoCache<
    PostDetail,
    ApiClientError
  >(queryKey.postDetail(id), () => PostApi.detail(id), {
    onSuccess: onLoaded,
  });

  const user = useUser();
  const { t } = useTranslation();
  const [openDateTimeModal, setOpenDateTimeModal] = useState(false);

  const [variant, setVariant] = useLocalStorage<PostPreviewVariant>(
    BrowserStorage.keys.Posts.PreviewVariant,
    'desktop',
  );

  const onEditDateTimeClick = () => {
    setOpenDateTimeModal(true);
  };
  return (
    <div
      className="tw-flex tw-flex-col tw-items-center"
      style={inlineStyle}
      data-name="posts-preview_real_preview"
      data-day={data?.date}
      data-post-created-at={data?.created_at}
    >
      {isLoading &&
        (loadingType === 'indicator' ? (
          <ActivityIndicator className="tw-h-8 tw-w-8 tw-py-6 " />
        ) : (
          <Skeleton />
        ))}
      {data && (
        <>
          <PostPreviewWithVariants
            content={{
              ...data,
              page: {
                id: data.page.id,
                name: data.page.name,
                type: data.page.type as PageNumberType,
                logo_src: data.page.logo?.src ?? null,
              },
              text: data.raw_text,
              attachments: coerceToPreviewAttachments(data.attachments),
            }}
            variant={variant}
            onVariantChange={setVariant}
            use12hourFormat={!user.useMeridianTime}
            dataCy={{
              postText: 'post-detail-preview-text',
              wrapper: 'post-detail-preview',
            }}
            onEditDateTimeClick={id ? onEditDateTimeClick : undefined}
          />
          <QuickPostEditDateTime
            open={openDateTimeModal}
            onClose={() => setOpenDateTimeModal(false)}
            post={data}
          />
        </>
      )}

      {isError && (
        <Alert variant="danger" className="tw-mt-6">
          {error?.userMessage ??
            `${t('somethingWentWrong')} ${t(
              'weWereUnablToLoadPreviewOfYourPost',
            )}`}
        </Alert>
      )}
    </div>
  );
};

export default Preview;
